// features/ui/uiSlice.ts
import { createAppSlice } from "../../app/createAppSlice";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../app/store";
import { AppType } from "@common/types/sharedTypes";
import { JSX } from "react/jsx-runtime";

interface UIState {
  /** <modalId, isOpen> dictionary - gives you unlimited named modals */
  modals: Record<string, boolean>;
  showOffCanvas: boolean;
  isFullscreen: boolean;
  breakpoint: number;
  isMobile: boolean;
  activeAccordionKeys: string[];
  accordionAllKeys: string[];
  isAccordionCollapsed: boolean;
  activeLink: string;
  createNew: AppType | null;
  /** The type and material last picked for a new file, so the next "New file" opens on them again.
      Kept in memory only, so a refresh goes back to the defaults. */
  lastCreateType: AppType | null;
  lastCreateMtrl: number | undefined;
  alerts: {
    id: string;
    type: string;
    status: string | JSX.Element;
    text: string | JSX.Element;
    context?: string;
    keep?: boolean;
  }[];
}

const initialState: UIState = {
  modals: {},
  showOffCanvas: false,
  isFullscreen: false,
  breakpoint: 768,
  isMobile: false,
  activeAccordionKeys: ["0", "1", "2", "3", "4"] as string[],
  accordionAllKeys: ["0", "1", "2", "3", "4", "5"] as string[], // Central list of ALL keys
  isAccordionCollapsed: false,
  activeLink: "/",
  createNew: null, // This is used to track if the user is creating a new item
  lastCreateType: null,
  lastCreateMtrl: undefined,
  alerts: [],
};

export const UISlice = createAppSlice({
  name: "uiSlice",
  initialState,
  reducers: {
    /** Open a modal by its ID */
    openModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = false;
    },
    toggleModal: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.modals[id] = !state.modals[id];
    },
    // --- OffCanvas Actions ---
    openOffCanvas: (s) => {
      s.showOffCanvas = true;
    },
    closeOffCanvas: (s) => {
      s.showOffCanvas = false;
    },
    toggleOffCanvas: (s) => {
      s.showOffCanvas = !s.showOffCanvas;
    },
    // --- Mobile screen size check ---
    setBreakpoint(state, action: PayloadAction<number>) {
      state.breakpoint = action.payload;
      state.isMobile = window.innerWidth < action.payload;
    },
    setIsMobile(state, action: PayloadAction<boolean>) {
      state.isMobile = action.payload;
    },
    // --- Fullscreen Actions ---
    enableFullscreen: (s) => {
      s.isFullscreen = true;
    },
    disableFullscreen: (s) => {
      s.isFullscreen = false;
    },
    toggleFullscreen: (s) => {
      s.isFullscreen = !s.isFullscreen;
    },
    // --- Accordion Actions ---
    toggleAccordionKey: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (state.activeAccordionKeys.includes(key)) {
        state.activeAccordionKeys = state.activeAccordionKeys.filter((k) => k !== key);
      } else {
        state.activeAccordionKeys.push(key);
      }
    },
    setAccordionKeys: (state, action: PayloadAction<string[]>) => {
      state.activeAccordionKeys = action.payload;
    },
    toggleCollapseAllAccordion: (state) => {
      if (state.isAccordionCollapsed) {
        // Expand all
        state.activeAccordionKeys = [...state.accordionAllKeys];
        state.isAccordionCollapsed = false;
      } else {
        // Collapse all
        state.activeAccordionKeys = [];
        state.isAccordionCollapsed = true;
      }
    },
    // ---- Set the active link for navigation ----
    setActiveLink: (state, action: PayloadAction<string>) => {
      state.activeLink = action.payload;
    },
    // ---- Create New Item ----
    setCreateNew: (state, action: PayloadAction<AppType | null>) => {
      state.createNew = action.payload;
      if (action.payload !== null) state.lastCreateType = action.payload;
    },
    setLastCreateMtrl: (state, action: PayloadAction<number | undefined>) => {
      state.lastCreateMtrl = action.payload;
    },
    // ---- Alerts ----
    addAlert: (state, action: PayloadAction<UIState["alerts"][number]>) => {
      const incoming = action.payload;

      // Duplicate prevention
      const exists = state.alerts.some((a) => a.text?.toString().trim() === incoming.text?.toString().trim());

      if (!exists) {
        state.alerts.push(incoming);
      }
    },
    removeAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter((a) => a.id !== action.payload);
    },
  },
  selectors: {
    /** Entire modal map (useful for debugging) */
    selectModals: (state: UIState) => state.modals,
    selectShowOffCanvas: (state) => state.showOffCanvas,
    selectFullscreen: (state) => state.isFullscreen,
    selectBreakPoint: (state) => state.breakpoint,
    selectIsMobile: (state) => state.isMobile,
    selectActiveAccordionKeys: (state) => state.activeAccordionKeys,
    selectIsAccordionCollapsed: (state) => state.isAccordionCollapsed,
    selectActiveLink: (state) => state.activeLink,
    selectCreateNew: (state) => state.createNew,
    selectLastCreateType: (state) => state.lastCreateType,
    selectLastCreateMtrl: (state) => state.lastCreateMtrl,
    selectAlerts: (state: UIState) => state.alerts,
  },
});

// Export the actions and selectors so they can be used in components
export const {
  openModal,
  closeModal,
  toggleModal,
  openOffCanvas,
  closeOffCanvas,
  toggleOffCanvas,
  setBreakpoint,
  setIsMobile,
  enableFullscreen,
  disableFullscreen,
  toggleFullscreen,
  toggleAccordionKey,
  setAccordionKeys,
  toggleCollapseAllAccordion,
  setActiveLink,
  setCreateNew,
  setLastCreateMtrl,
  addAlert,
  removeAlert,
} = UISlice.actions;

// selectors
export const {
  selectModals,
  selectShowOffCanvas,
  selectFullscreen,
  selectBreakPoint,
  selectIsMobile,
  selectActiveAccordionKeys,
  selectIsAccordionCollapsed,
  selectActiveLink,
  selectCreateNew,
  selectLastCreateType,
  selectLastCreateMtrl,
  selectAlerts,
} = UISlice.selectors;

/* ------------------------------------------------------------------ */
/* ▸ Parameterised selector (needs its own export)                    */
/*    You call it like: useSelector(selectModalById("appModal"))      */
/* ------------------------------------------------------------------ */
export const selectModalById = (id: string) => (state: RootState) => !!state.uiSlice.modals[id]; // ⬅️ adapt key if you mounted slice under a different name
